"use client";

import { logger } from "@/app/_lib/logger";
import posthog from "posthog-js";

export interface SubscriptionPersonProperties {
  subscription_status?: string;
  subscription_active?: boolean;
  plan_code?: string;
  payment_provider?: string;
}

/**
 * Subscription fields on this interface are managed exclusively by
 * PostHogPersonSync — do not pass them through identifyUser or you'll
 * race with the sync component on subscription changes.
 */
export interface UserProperties extends SubscriptionPersonProperties {
  email?: string;
  name?: string;
  [key: string]: any;
}

export function identifyUser(userId: string, properties: UserProperties) {
  posthog.identify(userId, properties);
}

/**
 * Update subscription-related person properties on the current PostHog
 * person without re-identifying. Call whenever user.subscription changes
 * so all subsequent events (including Delphi gateway-emitted ones) can be
 * sliced by subscription tier.
 *
 * Strips undefined keys so we never send `{ key: undefined }` to PostHog —
 * the unset semantics differ across posthog-js versions, and we want
 * "unknown" to leave the property untouched, not blanked out.
 */
export function setSubscriptionPersonProperties(
  properties: SubscriptionPersonProperties,
): void {
  const defined = Object.fromEntries(
    Object.entries(properties).filter(([, v]) => v !== undefined),
  );
  if (Object.keys(defined).length === 0) return;
  try {
    posthog.setPersonProperties(defined);
  } catch (err) {
    // Analytics failures must never break the protected layout. Surface
    // them so a PostHog outage / SDK bug is visible in console + Sentry
    // instead of silently dropping subscription tier from every event.
    logger.error("open.analytics.posthog_set_person_properties_failed", {
      feature: "analytics",
      message: "[posthog] setPersonProperties failed:",
      error: err,
    });
  }
}
